import { saveAs } from "file-saver";

import { captureException, captureMessage } from "@/error/sentry";
import { times } from "@/math/coordinates";
import { boardAspect, screenShotWidth } from "@/model/Settings";
import { useArtStore } from "@/store/art";
import { useBoardStore } from "@/store/board";
import { useBoardSizeStore } from "@/store/boardSize";
import { useSessionStore } from "@/store/session";
import { useToastStore } from "@/store/toast";
import { useZoomStore } from "@/store/zoom";
import { ignorePromise } from "@/utils/async/utils";

import {
  removeBoxShadows,
  resetBoxShadows,
  resetExplicitCurrentColor,
  resetExplicitSize,
  setExplicitCurrentColor,
  setExplicitSize,
  stickyNotes,
  svgElementsOnBoard,
} from "./screenshotDom";

export function screenShot() {
  useToastStore().show(/*$t*/ "message.generateImage", { duration: 0 });
  window.setTimeout(
    ignorePromise(async () => {
      try {
        await screenShotWithAdjustedDom();
      } finally {
        useToastStore().hide();
      }
    }),
    100,
  );
}

// adjust the DOM to avoid element styles that are not supported by html2canvas
async function screenShotWithAdjustedDom() {
  const boardStore = useBoardStore();
  const currentBoardId = boardStore.currentBoard().id;

  try {
    boardStore.setForceFullDetailsForBoardId(currentBoardId);

    // Wait for the browser to actually render the DOM changes
    await new Promise((resolve) => requestAnimationFrame(resolve));

    stickyNotes().forEach(removeBoxShadows);
    svgElementsOnBoard().forEach((svg) => {
      setExplicitCurrentColor(svg);
      setExplicitSize(svg);
    });
    await doScreenShot();
  } catch (fail) {
    captureException(fail);
  } finally {
    boardStore.setForceFullDetailsForBoardId(null);

    stickyNotes().forEach(resetBoxShadows);
    svgElementsOnBoard().forEach((svg) => {
      resetExplicitCurrentColor(svg);
      resetExplicitSize(svg);
    });
  }
}

async function doScreenShot() {
  const { default: html2canvas } = await import("@nidi/html2canvas");
  const scale = screenShotScale();
  const canvas = await html2canvas(document.body, {
    scale,
    logging: false,
    ignoreElements: (e) =>
      e.getAttribute("data-no-screenshot") !== null || e.classList?.contains("intercom-namespace"),
  });
  cutOutBoard(canvas, scale).toBlob((blob) => {
    if (blob) {
      saveAs(blob, screenShotName());
    } else {
      captureMessage("Could not generate screenshot blob");
    }
  });
}

function screenShotScale() {
  return screenShotWidth / (useZoomStore().factor * effectiveBoardWidth());
}

function effectiveBoardWidth() {
  return parseInt(document.getElementById("boards")!.style.width);
}

function cutOutBoard(canvas: HTMLCanvasElement, scale: number) {
  const target = document.createElement("canvas");
  target.width = screenShotWidth;
  target.height = screenShotWidth / boardAspect;
  const d = times(useBoardSizeStore().board.p0, scale);
  target.getContext("2d")!.drawImage(canvas, -d.x, -d.y);
  return target;
}

function screenShotName() {
  const session = safeFilename(useSessionStore().current.name);
  const art = useArtStore().isMultiArt ? "-" + safeFilename(useArtStore().current.name) : "";
  return `${session}${art}-${boardName()}.png`;
}

function safeFilename(s: string) {
  return s.replace(/[^A-Za-z0-9]+/g, "_");
}

function boardName() {
  const board = useBoardStore().currentBoard();
  switch (board.type) {
    case "team":
      return "team-" + safeFilename(board.team.name || "");
    case "flex":
      return safeFilename(board.name || "");
    default:
      return board.type;
  }
}
